import React, { useState, useEffect, useCallback, useMemo } from 'react';
import LinkButton from './LinkButton';
import './AdminPanel.css';

const AdminPanel = ({ onClose, backHref }) => {
  const [users, setUsers] = useState([]);
  const [allQuests, setAllQuests] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [grantedQuests, setGrantedQuests] = useState([]);
  const [grantPick, setGrantPick] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [creating, setCreating] = useState(false);

  const loadUsers = useCallback(async () => {
    const r = await fetch('/api/admin/users', { credentials: 'include' });
    if (!r.ok) throw new Error(`Failed to load users: ${r.status}`);
    return r.json();
  }, []);

  const loadAllQuests = useCallback(async () => {
    const r = await fetch('/api/admin/all-quests', { credentials: 'include' });
    if (!r.ok) throw new Error(`Failed to load quests: ${r.status}`);
    return r.json();
  }, []);

  const loadGrantedQuests = useCallback(async (userId) => {
    const r = await fetch(`/api/admin/users/${userId}/quests`, { credentials: 'include' });
    if (!r.ok) throw new Error(`Failed to load user quests: ${r.status}`);
    return r.json();
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [u, q] = await Promise.all([loadUsers(), loadAllQuests()]);
        setUsers(u);
        setAllQuests(q);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [loadUsers, loadAllQuests]);

  useEffect(() => {
    if (selectedUserId == null) {
      setGrantedQuests([]);
      return;
    }
    (async () => {
      try {
        const list = await loadGrantedQuests(selectedUserId);
        setGrantedQuests(list);
      } catch (e) {
        setError(e.message);
      }
    })();
  }, [selectedUserId, loadGrantedQuests]);

  const selectedUser = users.find((u) => u.id === selectedUserId) || null;

  const grantedSet = useMemo(
    () => new Set(grantedQuests.map((g) => g.quest_id)),
    [grantedQuests]
  );

  // Deduplicate quests by quest_id (a quest may exist in both quests and quests_test).
  const uniqueQuestsForPicker = useMemo(() => {
    const seen = new Map();
    for (const q of allQuests) {
      if (!seen.has(q.quest_id)) seen.set(q.quest_id, q);
    }
    return Array.from(seen.values()).filter((q) => !grantedSet.has(q.quest_id));
  }, [allQuests, grantedSet]);

  const createUser = async (e) => {
    e.preventDefault();
    if (!newUsername.trim() || !newPassword) return;
    setCreating(true);
    setError(null);
    try {
      const r = await fetch('/api/admin/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ username: newUsername.trim(), password: newPassword }),
      });
      const j = await r.json();
      if (!r.ok) {
        setError(j.error || `Failed to create user: ${r.status}`);
        return;
      }
      setUsers((prev) => [...prev, j]);
      setNewUsername('');
      setNewPassword('');
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const toggleUserFlag = async (user, field) => {
    const body = {};
    body[field] = !user[field];
    const r = await fetch(`/api/admin/users/${user.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(field === 'is_admin'
        ? { isAdmin: body.is_admin }
        : { canPushToProd: body.can_push_to_prod }),
    });
    if (!r.ok) {
      const j = await r.json().catch(() => ({}));
      setError(j.error || `Failed to update user: ${r.status}`);
      return;
    }
    const updated = await r.json();
    setUsers((prev) =>
      prev.map((u) => (u.id === updated.id
        ? { ...u, is_admin: updated.is_admin, can_push_to_prod: updated.can_push_to_prod }
        : u))
    );
  };

  const grantQuest = async () => {
    if (!selectedUserId || !grantPick) return;
    const r = await fetch(`/api/admin/users/${selectedUserId}/quests`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ questId: grantPick }),
    });
    if (!r.ok) {
      const j = await r.json().catch(() => ({}));
      setError(j.error || `Failed to grant: ${r.status}`);
      return;
    }
    setGrantedQuests((prev) => [...prev, { quest_id: grantPick, granted_at: new Date().toISOString() }]);
    setUsers((prev) =>
      prev.map((u) => (u.id === selectedUserId ? { ...u, quest_count: (u.quest_count || 0) + 1 } : u))
    );
    setGrantPick('');
  };

  const revokeQuest = async (questId) => {
    if (!selectedUserId) return;
    const r = await fetch(
      `/api/admin/users/${selectedUserId}/quests/${encodeURIComponent(questId)}`,
      { method: 'DELETE', credentials: 'include' }
    );
    if (!r.ok) {
      const j = await r.json().catch(() => ({}));
      setError(j.error || `Failed to revoke: ${r.status}`);
      return;
    }
    setGrantedQuests((prev) => prev.filter((g) => g.quest_id !== questId));
    setUsers((prev) =>
      prev.map((u) => (u.id === selectedUserId ? { ...u, quest_count: Math.max(0, (u.quest_count || 0) - 1) } : u))
    );
  };

  const questNameById = useMemo(() => {
    const m = new Map();
    for (const q of allQuests) {
      if (!m.has(q.quest_id)) m.set(q.quest_id, q.quest_name);
    }
    return m;
  }, [allQuests]);

  return (
    <div className="admin-panel">
      <div className="admin-panel-header">
        <h2>Admin Panel</h2>
        <LinkButton className="back-button" href={backHref} onClick={onClose}>Back to Editor</LinkButton>
      </div>

      {error && <div className="error">{error}</div>}

      <form className="admin-create-user-form" onSubmit={createUser}>
        <h3>New user</h3>
        <input
          type="text"
          placeholder="Username"
          value={newUsername}
          onChange={(e) => setNewUsername(e.target.value)}
          required
        />
        <input
          type="password"
          placeholder="Password (min 6 chars)"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          minLength={6}
          required
        />
        <button type="submit" disabled={creating}>
          {creating ? 'Creating…' : 'Create user'}
        </button>
      </form>

      {loading ? (
        <div>Loading...</div>
      ) : (
        <>
          <table className="admin-users-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Admin</th>
                <th>Push to Prod</th>
                <th>Quests</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr
                  key={u.id}
                  className={u.id === selectedUserId ? 'selected' : ''}
                  onClick={() => setSelectedUserId(u.id)}
                >
                  <td>{u.username}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={!!u.is_admin}
                      onChange={() => toggleUserFlag(u, 'is_admin')}
                    />
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={!!u.can_push_to_prod}
                      onChange={() => toggleUserFlag(u, 'can_push_to_prod')}
                    />
                  </td>
                  <td>{u.is_admin ? 'all' : u.quest_count}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {selectedUser ? (
            <div className="admin-user-quests">
              <h3>
                Quest access for <code>{selectedUser.username}</code>
                {selectedUser.is_admin && ' (admin — sees everything regardless)'}
              </h3>

              {grantedQuests.length === 0 ? (
                <p className="muted">No explicit quest grants yet.</p>
              ) : (
                <ul className="admin-granted-list">
                  {grantedQuests.map((g) => (
                    <li key={g.quest_id}>
                      <span>
                        <strong>{questNameById.get(g.quest_id) || '(deleted quest)'}</strong>
                        <span className="muted"> — {g.quest_id}</span>
                      </span>
                      <button className="revoke-btn" onClick={() => revokeQuest(g.quest_id)}>
                        Revoke
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              <div className="admin-grant-row">
                <select value={grantPick} onChange={(e) => setGrantPick(e.target.value)}>
                  <option value="">— pick a quest to grant —</option>
                  {uniqueQuestsForPicker.map((q) => (
                    <option key={q.quest_id} value={q.quest_id}>
                      {q.quest_name} ({q.quest_id})
                    </option>
                  ))}
                </select>
                <button onClick={grantQuest} disabled={!grantPick}>
                  Grant access
                </button>
              </div>
            </div>
          ) : (
            <p className="muted">Select a user above to manage their quest access.</p>
          )}
        </>
      )}
    </div>
  );
};

export default AdminPanel;
